// Thin wrapper around citation.js (MIT, https://citation.js.org): parses BibTeX /
// RIS (e.g. Google Scholar "Cite → BibTeX"), resolves DOIs and formats CSL
// bibliographies. Loaded on demand so it stays out of the main bundle.
//
// Styles beyond citation.js's built-ins are official CSL files
// (https://github.com/citation-style-language/styles, CC BY-SA 3.0): the quick picks
// are served from public/csl, and any of the ~10,000 journal styles in the Zotero
// Style Repository is fetched through the backend proxy. Each is registered the
// first time it is used.

import { axiosService } from 'shared/helpers/axios';

let citePromise = null;

const loadCite = () => {
  if (!citePromise) {
    citePromise = (async () => {
      const core = await import('@citation-js/core');
      await Promise.all([
        import('@citation-js/plugin-bibtex'),
        import('@citation-js/plugin-ris'),
        import('@citation-js/plugin-doi'),
        import('@citation-js/plugin-csl'),
      ]);
      return { Cite: core.Cite, templates: core.plugins.config.get('@csl').templates };
    })();
  }
  return citePromise;
};

// ---- Styles -------------------------------------------------------------------
//
// Quick picks shown in the style dialog. `numeric` only groups them in the UI; the
// actual rendering is derived from each style's CSL (see styleKind).

export const CITATION_STYLES = [
  { value: 'vancouver', label: 'Vancouver', hint: 'Medicine (ICMJE)', numeric: true },
  { value: 'nature', label: 'Nature', hint: 'Nature journals', file: 'nature.csl', numeric: true },
  { value: 'science', label: 'Science', hint: 'Science / AAAS', file: 'science.csl', numeric: true },
  { value: 'cell', label: 'Cell', hint: 'Cell Press', file: 'cell.csl', numeric: true },
  { value: 'ieee', label: 'IEEE', hint: 'Engineering, computing', file: 'ieee.csl', numeric: true },
  { value: 'american-medical-association', label: 'AMA 11th', hint: 'JAMA, medicine', file: 'american-medical-association.csl', numeric: true },
  { value: 'apa', label: 'APA 7th', hint: 'Psychology, social sciences' },
  { value: 'harvard-cite-them-right', label: 'Harvard', hint: 'Cite Them Right', file: 'harvard-cite-them-right.csl' },
  { value: 'chicago-author-date', label: 'Chicago', hint: 'Author–date, 18th ed.', file: 'chicago-author-date.csl' },
  { value: 'modern-language-association', label: 'MLA 9th', hint: 'Humanities', file: 'modern-language-association.csl' },
];

export const DEFAULT_CITATION_STYLE = 'vancouver';
const BUILTIN_STYLES = new Set(['apa', 'vancouver']); // shipped inside citation.js

/** Quick-pick entry, or a journal style from the repository (label = its title). */
export const getStyle = (value, title) =>
  CITATION_STYLES.find((s) => s.value === value)
  || (value ? { value, label: title || value, hint: 'Journal style', remote: true } : getStyle(DEFAULT_CITATION_STYLE));

export const isQuickStyle = (value) => CITATION_STYLES.some((s) => s.value === value);

const loadedStyles = new Set();

/** Loads citation.js and registers the style's CSL if needed. */
const ensureStyle = async (value) => {
  const { Cite, templates } = await loadCite();
  const style = getStyle(value);
  if (!BUILTIN_STYLES.has(style.value) && !loadedStyles.has(style.value)) {
    let xml;
    if (style.file) {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/csl/${style.file}`);
      if (!res.ok) throw new Error(`Could not load the ${style.label} style`);
      xml = await res.text();
    } else {
      const res = await axiosService.get(`/proposal/journal-styles/${encodeURIComponent(style.value)}/csl/`, {
        responseType: 'text', transformResponse: (r) => r,
      });
      xml = res.data;
    }
    templates.add(style.value, xml);
    loadedStyles.add(style.value);
  }
  return { Cite, template: style.value, templates };
};

// Three dummy works used to read a style's in-text and bibliography conventions
const DUMMY_WORKS = [1, 2, 3].map((n) => ({
  id: `dummy${n}`, type: 'article-journal', title: `Title ${n}`, 'container-title': 'Journal',
  author: [{ family: `Author${n}`, given: 'A' }], issued: { 'date-parts': [[2000 + n]] },
}));

const styleKinds = new Map();

/**
 * How a style renders citations: { numeric, sup, pattern: { prefix, suffix, sep, range },
 * bracketList }. Numbered styles keep our stable numbers, so the pattern (e.g. "[1,2]",
 * "(1–3)", "[1], [2]") is read from the style's own output for dummy works.
 */
export const styleKind = async (value) => {
  if (styleKinds.has(value)) return styleKinds.get(value);
  const { Cite, template, templates } = await ensureStyle(value);
  const xml = String(templates.get(template) || '');
  const citationBlock = (xml.match(/<citation[\s\S]*?<\/citation>/) || [''])[0];
  const kind = {
    numeric: /citation-format="numeric"/.test(xml),
    sup: /vertical-align="sup"/.test(citationBlock),
    pattern: null,
    bracketList: false,
  };
  if (kind.numeric) {
    const cite = (n) => new Cite(DUMMY_WORKS.slice(0, n)).format('citation', { format: 'text', template, lang: 'en-US' });
    const [one, two, three] = [cite(1), cite(2), cite(3)];
    const at = one.indexOf('1');
    if (at >= 0) {
      const prefix = one.slice(0, at);
      const suffix = one.slice(at + 1);
      const middle = (text, a, b) => {
        const i = text.indexOf(a, prefix.length);
        const j = text.indexOf(b, i + 1);
        return i >= 0 && j > i ? text.slice(i + 1, j) : null;
      };
      kind.pattern = {
        prefix,
        suffix,
        sep: middle(two, '1', '2') ?? ', ',
        range: three.includes('2') ? null : middle(three, '1', '3'),
      };
    } else {
      kind.pattern = { prefix: '[', suffix: ']', sep: ', ', range: '–' };
    }
    const bib = new Cite(DUMMY_WORKS[0]).format('bibliography', { format: 'text', template, lang: 'en-US' }).trim();
    kind.bracketList = /^\[1\]/.test(bib);
  }
  styleKinds.set(value, kind);
  return kind;
};

// ---- Import -------------------------------------------------------------------

const DOI_RE = /10\.\d{4,9}\/[^\s"<>]+/gi;

export const extractDois = (text) => Array.from(new Set((text.match(DOI_RE) || []).map((d) => d.replace(/[.,;]+$/, ''))));

const newId = () => `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// Strip citation.js internals and wrap as a stored reference
const toReference = (csl) => {
  const { _graph, ...clean } = csl; // eslint-disable-line no-unused-vars
  return { id: newId(), csl: clean, doi: clean.DOI || '', text: '' };
};

/** Parse pasted BibTeX / RIS / CSL-JSON (or a list of DOIs) into references. */
export const parseReferences = async (input) => {
  const { Cite } = await loadCite();
  const cite = await Cite.async(input.trim());
  return cite.data.map(toReference);
};

// ── EndNote library formats (citation.js has no plugin for these) ─────────────

const ENDNOTE_TYPES = {
  'journal article': 'article-journal', book: 'book', 'book section': 'chapter', 'edited book': 'book',
  'conference proceedings': 'paper-conference', 'conference paper': 'paper-conference', thesis: 'thesis',
  report: 'report', 'web page': 'webpage', 'electronic article': 'article-journal', patent: 'patent',
  'newspaper article': 'article-newspaper', 'magazine article': 'article-magazine', dataset: 'dataset',
};
const cslType = (endnoteType = '') => ENDNOTE_TYPES[endnoteType.trim().toLowerCase()] || 'article-journal';

/** "Smith, John A." / "John A. Smith" / "WHO" → CSL name */
const cslName = (raw = '') => {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (!name) return null;
  if (name.includes(',')) {
    const [family, ...rest] = name.split(',');
    return { family: family.trim(), given: rest.join(',').trim() };
  }
  const parts = name.split(' ');
  return parts.length === 1 ? { literal: name } : { family: parts.pop(), given: parts.join(' ') };
};

const cslFromFields = (f) => {
  const csl = { type: cslType(f.type) };
  if (f.title) csl.title = f.title;
  if (f.authors?.length) csl.author = f.authors.map(cslName).filter(Boolean);
  if (f.editors?.length) csl.editor = f.editors.map(cslName).filter(Boolean);
  if (f.container) csl['container-title'] = f.container;
  const year = (f.year || '').match(/\d{4}/);
  if (year) csl.issued = { 'date-parts': [[Number(year[0])]] };
  if (f.volume) csl.volume = f.volume;
  if (f.issue) csl.issue = f.issue;
  if (f.pages) csl.page = f.pages;
  if (f.publisher) csl.publisher = f.publisher;
  if (f.place) csl['publisher-place'] = f.place;
  if (f.url) csl.URL = f.url;
  if (f.abstract) csl.abstract = f.abstract;
  const doi = (f.doi || '').match(/10\.\d{4,9}\/\S+/);
  if (doi) csl.DOI = doi[0].replace(/[.,;]+$/, '');
  return csl;
};

/** EndNote tagged export (.enw / "Refer"): %0 type, %A author, %T title, … records split by blank lines. */
export const parseEndNoteTagged = (text) => {
  const records = text.replace(/\r/g, '').split(/\n\s*\n(?=%)/).map((r) => r.trim()).filter((r) => r.startsWith('%'));
  return records.map((record) => {
    const f = { authors: [], editors: [] };
    record.split('\n').forEach((line) => {
      const m = line.match(/^%(\S)\s+(.*)$/);
      if (!m) return;
      const [, tag, value] = m;
      switch (tag) {
        case '0': f.type = value; break;
        case 'A': f.authors.push(value); break;
        case 'E': f.editors.push(value); break;
        case 'T': f.title = value; break;
        case 'J': f.container = value; break;
        case 'B': f.container = f.container || value; break;
        case 'D': f.year = value; break;
        case 'V': f.volume = value; break;
        case 'N': f.issue = value; break;
        case 'P': f.pages = value; break;
        case 'I': f.publisher = value; break;
        case 'C': f.place = value; break;
        case 'U': f.url = value; break;
        case 'R': f.doi = value; break;
        case 'X': f.abstract = value; break;
        default:
      }
    });
    return toReference(cslFromFields(f));
  }).filter((r) => r.csl.title || r.csl.author?.length);
};

/** EndNote XML export (File → Export → XML). */
export const parseEndNoteXml = (text) => {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('Not valid XML');
  const val = (el, selector) => el.querySelector(selector)?.textContent?.trim() || '';
  const all = (el, selector) => Array.from(el.querySelectorAll(selector)).map((n) => n.textContent.trim()).filter(Boolean);
  return Array.from(doc.querySelectorAll('records > record')).map((rec) => toReference(cslFromFields({
    type: rec.querySelector('ref-type')?.getAttribute('name') || '',
    title: val(rec, 'titles > title'),
    container: val(rec, 'titles > secondary-title') || val(rec, 'periodical > full-title'),
    authors: all(rec, 'contributors > authors > author'),
    editors: all(rec, 'contributors > secondary-authors > author'),
    year: val(rec, 'dates > year'),
    volume: val(rec, 'volume'),
    issue: val(rec, 'number'),
    pages: val(rec, 'pages'),
    publisher: val(rec, 'publisher'),
    place: val(rec, 'pub-location'),
    url: val(rec, 'urls related-urls url') || val(rec, 'urls url'),
    doi: val(rec, 'electronic-resource-num'),
    abstract: val(rec, 'abstract'),
  }))).filter((r) => r.csl.title || r.csl.author?.length);
};

/** Any supported library file: BibTeX, RIS, CSL-JSON, EndNote XML or EndNote tagged (.enw). */
export const parseReferenceFile = async (name, text) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const head = text.replace(/^﻿/, '').trimStart();
  if (ext === 'xml' || head.startsWith('<?xml') || head.startsWith('<xml')) return parseEndNoteXml(text);
  if (ext === 'enw' || /^%0\s/.test(head)) return parseEndNoteTagged(text);
  return parseReferences(text);
};

export const referencesFromDois = async (dois) => {
  const { Cite } = await loadCite();
  const cite = await Cite.async(dois);
  return cite.data.map(toReference);
};

// ---- Formatting -----------------------------------------------------------------

/** Formatted single bibliography entry (no leading number). */
export const formatReference = async (ref, style) => {
  if (!ref.csl) return ref.text || '';
  const { Cite, template } = await ensureStyle(style);
  const out = new Cite(ref.csl).format('bibliography', { format: 'text', template, lang: 'en-US' });
  return out.trim().replace(/^\[?\d+\]?\.?\s+/, '');
};

export const formatAll = async (refs, style) => {
  await ensureStyle(style);
  return Promise.all(refs.map((r) => formatReference(r, style)));
};

/**
 * Body for the References section in the given style.
 * Numeric styles keep citation order (so numbers match the text); author–date
 * styles are alphabetical and unnumbered.
 */
export const formatBibliographyMarkdown = async (citedRefs, styleValue) => {
  const kind = await styleKind(styleValue);
  const entries = await formatAll(citedRefs, styleValue);
  if (kind.numeric && kind.bracketList) return entries.map((t, i) => `[${i + 1}] ${t}`).join('\n\n');
  // Consecutive "n. " lines form one numbered list in the editor's markdown
  if (kind.numeric) return entries.map((t, i) => `${i + 1}. ${t}`).join('\n');
  return [...entries].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })).join('\n\n');
};

export const toBibtex = async (refs) => {
  const { Cite } = await loadCite();
  const structured = refs.filter((r) => r.csl).map((r) => r.csl);
  const manual = refs.filter((r) => !r.csl).map((r) => `% ${r.text}`);
  const bib = structured.length ? new Cite(structured).format('bibtex') : '';
  return [bib, ...manual].filter(Boolean).join('\n');
};

/** Normalised key for duplicate detection. */
export const referenceKey = (ref) => {
  if (ref.doi) return `doi:${ref.doi.toLowerCase()}`;
  const title = ref.csl?.title || ref.text || '';
  return `t:${title.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 80)}`;
};

/** Crossref open search (no key needed). Returns lightweight hits with DOIs. */
export const searchCrossref = async (query) => {
  const url = `https://api.crossref.org/works?rows=10&query.bibliographic=${encodeURIComponent(query)}`
    + '&select=DOI,title,author,issued,container-title,type';
  const res = await fetch(url);
  if (!res.ok) throw new Error('Crossref search failed');
  const json = await res.json();
  return (json.message?.items || []).map((item) => ({
    doi: item.DOI,
    title: item.title?.[0] || '(untitled)',
    authors: (item.author || []).slice(0, 3).map((a) => a.family || a.name).filter(Boolean).join(', ')
      + ((item.author || []).length > 3 ? ' et al.' : ''),
    year: item.issued?.['date-parts']?.[0]?.[0] || '',
    venue: item['container-title']?.[0] || '',
  }));
};

export const googleScholarUrl = (query) => `https://scholar.google.com/scholar?q=${encodeURIComponent(query)}`;

// ---- In-text citations ------------------------------------------------------
//
// Invariant: cited references occupy the first k positions of the bibliography,
// in order of first citation, so a reference's in-text number [n] is simply its
// position and never changes once cited. Uncited references follow, unnumbered.
//
// The text always stores these canonical [n] markers; the chosen style is applied
// when rendering (preview, export), so switching styles never rewrites the text.

export const citedCount = (refs) => refs.filter((r) => r.cited).length;

/**
 * Mark references as cited (moving newly cited ones to the end of the cited block).
 * Returns the updated list, the in-text numbers for `ids`, and whether anything changed.
 */
export const applyCitations = (refs, ids) => {
  const list = [...refs];
  let changed = false;
  ids.forEach((id) => {
    const idx = list.findIndex((r) => r.id === id);
    if (idx === -1 || list[idx].cited) return;
    const [ref] = list.splice(idx, 1);
    list.splice(citedCount(list), 0, { ...ref, cited: true });
    changed = true;
  });
  const numbers = ids
    .map((id) => list.findIndex((r) => r.id === id) + 1)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  return { references: list, numbers, changed };
};

/** "[1, 3, 4]" canonical marker for the text. */
export const citationMarker = (numbers) => `[${numbers.join(', ')}]`;

/** Body for the References section: the cited references in the publication's style. */
export const citedReferencesMarkdown = async (refs, style) =>
  formatBibliographyMarkdown(refs.filter((r) => r.cited), style);

const MARKER_RE = /\[(\d+(?:\s*[,–-]\s*\d+)*)\]/g;

export const parseMarkerNumbers = (inner) => {
  const numbers = [];
  inner.split(',').forEach((part) => {
    const [a, b] = part.split(/[–-]/).map((x) => parseInt(x.trim(), 10));
    if (Number.isNaN(a)) return;
    if (b && b > a) for (let n = a; n <= b; n += 1) numbers.push(n);
    else numbers.push(a);
  });
  return Array.from(new Set(numbers)).sort((x, y) => x - y);
};

/**
 * The full references behind an in-text marker such as "[2, 4–5]": ["2. Smith J. …", "4. …", "5. …"].
 * refs is the bibliography (number n = refs[n - 1]); texts maps reference id -> formatted reference.
 */
export const referencesForMarker = (marker, refs, texts) => parseMarkerNumbers(String(marker).slice(1, -1))
  .filter((n) => refs[n - 1])
  .map((n) => {
    const r = refs[n - 1];
    return `${n}. ${texts?.[r.id] || r.text || r.csl?.title || 'Untitled reference'}`;
  });

// 1,2,3,5 -> [[1,3],[5,5]]
const toRuns = (numbers) => {
  const runs = [];
  numbers.forEach((n) => {
    const last = runs[runs.length - 1];
    if (last && n === last[1] + 1) last[1] = n;
    else runs.push([n, n]);
  });
  return runs;
};

const SUPERSCRIPT = { 0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹', ',': '˒', '–': '⁻', '-': '⁻' };
const toSuperscript = (text) => text.split('').map((c) => SUPERSCRIPT[c] ?? c).join('');

const formatNumeric = (numbers, kind) => {
  const { prefix, suffix, sep, range } = kind.pattern;
  const parts = [];
  toRuns(numbers).forEach(([a, b]) => {
    if (range && b - a >= 2) parts.push(`${a}${range}${b}`);
    else for (let n = a; n <= b; n += 1) parts.push(String(n));
  });
  const text = `${prefix}${parts.join(sep)}${suffix}`;
  return kind.sup ? toSuperscript(text) : text;
};

/**
 * Map every canonical marker found in `bodies` to its rendering in `styleValue`.
 * refs is the full bibliography (cited first). Unknown numbers are left as-is.
 */
export const buildInTextMap = async (bodies, refs, styleValue) => {
  const markers = new Set();
  bodies.forEach((body) => (body || '').replace(MARKER_RE, (m) => { markers.add(m); return m; }));
  const map = new Map();
  if (!markers.size) return map;

  const kind = await styleKind(styleValue);
  const { Cite, template } = await ensureStyle(styleValue);
  markers.forEach((marker) => {
    const numbers = parseMarkerNumbers(marker.slice(1, -1)).filter((n) => refs[n - 1]?.cited);
    if (!numbers.length) return;
    if (kind.numeric) {
      map.set(marker, formatNumeric(numbers, kind));
      return;
    }
    const cited = numbers.map((n) => refs[n - 1]);
    if (cited.some((r) => !r.csl)) return; // manual entries have no author/year to show
    try {
      map.set(marker, new Cite(cited.map((r) => r.csl)).format('citation', { format: 'text', template, lang: 'en-US' }));
    } catch { /* leave the canonical marker */ }
  });
  return map;
};

const LEADING_SPACE_MARKER_RE = /([ \t]*)(\[\d+(?:\s*[,–-]\s*\d+)*\])/g;
const SUPERSCRIPT_START_RE = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]/;

/**
 * Replace canonical markers in a markdown body using a map from buildInTextMap.
 * Superscript citations attach directly to the preceding word ("vision¹").
 */
export const applyInTextMap = (body, map) => {
  if (!map || !map.size) return body;
  return (body || '').replace(LEADING_SPACE_MARKER_RE, (whole, space, marker) => {
    const rendered = map.get(marker);
    if (rendered === undefined) return whole;
    return SUPERSCRIPT_START_RE.test(rendered) ? rendered : `${space}${rendered}`;
  });
};

// ---- EndNote ------------------------------------------------------------------------
//
// EndNote's "unformatted citations": {FirstAuthor, Year} placeholders in the Word file are
// matched against the user's EndNote library (imported from our RIS) when they run
// "Update Citations and Bibliography", which also builds the reference list.

const endNoteCitation = (ref) => {
  const csl = ref.csl || {};
  const first = (csl.author || [])[0] || {};
  const name = first.family || first.literal || (csl.title || ref.text || 'Anon').split(/\s+/)[0];
  const year = csl.issued?.['date-parts']?.[0]?.[0] || csl.issued?.literal || 'n.d.';
  return `${name}, ${year}`.replace(/[{}]/g, '');
};

/** Map canonical markers to EndNote temporary citations, e.g. "[1, 2]" -> "{LeCun, 2015; He, 2016}". */
export const buildEndNoteMap = (bodies, refs) => {
  const map = new Map();
  bodies.forEach((body) => (body || '').replace(MARKER_RE, (marker) => {
    const cited = parseMarkerNumbers(marker.slice(1, -1)).map((n) => refs[n - 1]).filter((r) => r?.cited);
    if (cited.length) map.set(marker, `{${cited.map(endNoteCitation).join('; ')}}`);
    return marker;
  }));
  return map;
};

/** RIS file for EndNote / Zotero / Mendeley import. Manual references become generic records. */
export const toRis = async (refs) => {
  const { Cite } = await loadCite();
  const structured = refs.filter((r) => r.csl).map((r) => r.csl);
  const manual = refs.filter((r) => !r.csl).map((r) => `TY  - GEN\nTI  - ${r.text}\n${r.doi ? `DO  - ${r.doi}\n` : ''}ER  - \n`);
  const ris = structured.length ? new Cite(structured).format('ris') : '';
  return [ris, ...manual].filter(Boolean).join('\n');
};
